import type { IScannerControls } from "@zxing/browser";
import type { Result } from "@zxing/library";

// Camera lifecycle for the barcode flow (plan todo 16), isolated behind an
// injected reader + cleaner so the exact cleanup contract (controls.stop +
// cleanVideoSource, cancelled-flag race guard) is unit-testable in node
// without a DOM. BarcodeScanner.tsx wires this to a real
// BrowserMultiFormatReader and BrowserCodeReader.cleanVideoSource.

export const SCANNER_CONSTRAINTS: MediaStreamConstraints = {
  video: { facingMode: { ideal: "environment" } },
  audio: false,
};

// Structural subset of BrowserMultiFormatReader used by the session.
export interface ScannerReader {
  decodeFromConstraints(
    constraints: MediaStreamConstraints,
    video: HTMLVideoElement,
    callback: (result: Result | undefined, error: unknown, controls: IScannerControls) => void,
  ): Promise<IScannerControls>;
}

export interface ScannerSessionOptions {
  readonly reader: ScannerReader;
  readonly video: HTMLVideoElement;
  readonly onDecode: (barcode: string) => void;
  readonly onFailure: (error: unknown) => void;
  // Production passes BrowserCodeReader.cleanVideoSource; tests pass a spy.
  readonly cleanVideo: (video: HTMLVideoElement) => void;
}

export interface ScannerSession {
  // Idempotent: stops the decode loop (once) and ALWAYS releases the video
  // element's stream so no camera stays alive after unmount/close.
  readonly stop: () => void;
}

export function startScannerSession(options: ScannerSessionOptions): ScannerSession {
  let finished = false;
  let controls: IScannerControls | null = null;
  let controlsStopped = false;

  const stopControls = () => {
    if (controlsStopped) return;
    controlsStopped = true;
    controls?.stop();
  };

  options.reader
    .decodeFromConstraints(SCANNER_CONSTRAINTS, options.video, (result) => {
      if (finished || result === undefined) return;
      // Decode once: stop the loop immediately after the first hit so no
      // duplicate lookups fire for the same frame sequence.
      finished = true;
      stopControls();
      const text = result.getText();
      console.log("[Frontend][BarcodeScanner] Barcode decoded from camera", {
        digits: text.length,
      });
      options.onDecode(text);
    })
    .then((resolved) => {
      // Race guard: the session may have been stopped (or already decoded)
      // while the camera stream was still starting.
      if (finished) {
        resolved.stop();
        return;
      }
      controls = resolved;
    })
    .catch((error: unknown) => {
      if (finished) return;
      finished = true;
      console.warn("[Frontend][BarcodeScanner] Camera stream could not start", {
        error: error instanceof Error ? error.name : String(error),
      });
      options.onFailure(error);
    });

  return {
    stop: () => {
      finished = true;
      stopControls();
      options.cleanVideo(options.video);
    },
  };
}
